import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { scaleForZoom } from "../composables/ZoomLevels";

// ISEC LPIS layers for the farm map, on ISEC's ArcGIS Online feature service.
// (DAFM LPIS comes from this app's own server; see DafmLpisSource.js.)
//
// Two FeatureLayers share the service:
// - context: every parcel in grey, drawn only from farm-level zoom inwards so
//   the map isn't asked for the whole country when zoomed out;
// - linked: only the farmer's herd in pink, drawn at every zoom so a farmer
//   with land in several counties still sees it all.

// Context parcels draw from about web-mercator zoom 14 inwards.
export const LPIS_CONTEXT_MIN_SCALE = scaleForZoom(14);

export const PINK = [242, 105, 237];
export const GREY = [185, 185, 185];

export const greyFill = {
  type: "simple-fill",
  style: "solid",
  color: [...GREY, 0.3],
  outline: { color: GREY, width: 1 },
};

export const linkedSymbol = {
  type: "simple-fill",
  style: "solid",
  color: [...PINK, 0.15],
  outline: { color: PINK, width: 2 },
};

export const labelSymbol = {
  type: "text",
  color: "black",
  font: { size: "12px", family: "Arial", weight: "bold" },
};

// The ISEC layer keeps the styling it has always had.
const isecRenderer = {
  type: "unique-value",
  valueExpression: "When($feature.Commonage == 'Y', 'commonage', 'default')",
  defaultSymbol: greyFill,
  uniqueValueInfos: [
    {
      value: "commonage",
      symbol: {
        type: "simple-fill",
        style: "solid",
        color: [255, 255, 0, 0.2],
        outline: { color: GREY, width: 1 },
      },
    },
  ],
};

const OUT_FIELDS = ["FID", "app_herd", "area_claim", "parcel_id", "parc_label", "commonage"];

// SQL string literal with single quotes doubled.
export function sqlString(value) {
  return "'" + String(value ?? "").replace(/'/g, "''") + "'";
}

// Whole positive numbers only.
export function cleanIds(ids) {
  return [...new Set((ids || []).map(Number).filter((id) => Number.isInteger(id) && id > 0))];
}

class LPISLayerBuilder {
  // The ISEC layer has always loaded with the global ArcGIS key set in
  // BaseView.js, so no key is set on the layer itself.
  constructor(url) {
    this.url = url;
    this.herdField = "app_herd";
    this.minScale = LPIS_CONTEXT_MIN_SCALE;
  }

  getLayers() {
    if (this.layers) return this.layers;

    const context = new FeatureLayer({
      url: this.url,
      outFields: OUT_FIELDS,
      title: "LPIS",
      minScale: this.minScale,
      maxScale: 0,
      renderer: isecRenderer,
      labelingInfo: [
        {
          labelExpressionInfo: {
            expression: "$feature.parcel_id + TextFormatting.NewLine + $feature.area_claim + ' ha'",
          },
          maxScale: 0,
          minScale: 50000,
          symbol: labelSymbol,
        },
      ],
    });

    const linked = new FeatureLayer({
      url: this.url,
      outFields: OUT_FIELDS,
      title: "Farmer LPIS",
      definitionExpression: "1=0",
      renderer: { type: "simple", symbol: linkedSymbol },
      legendEnabled: false,
    });

    this.layers = { context, linked };
    return this.layers;
  }

  herdWhere(herd) {
    return `${this.herdField} = ${sqlString(herd)}`;
  }

  setLinkedWhere(where) {
    const { linked } = this.getLayers();
    if (linked.definitionExpression !== where) {
      linked.definitionExpression = where;
    }
  }

  async queryHolding(herd, { geometry = false } = {}) {
    const { context } = this.getLayers();
    await context.load();
    const query = context.createQuery();
    query.where = this.herdWhere(herd);
    query.outFields = [...new Set([...OUT_FIELDS, context.objectIdField])];
    query.returnGeometry = geometry;
    const result = await context.queryFeatures(query);
    return result.features;
  }

  // Object ids for highlighting. Both layers share the service, so the ids
  // hold in either.
  objectIdsIn(layer, features) {
    const field = layer.objectIdField;
    return features.map((feature) => feature.attributes?.[field]).filter((id) => id !== undefined);
  }

  // Total claimed area and the parcels of a herd, for zooming to them.
  async calculateTotalAreaClaim(herd) {
    try {
      const features = await this.queryHolding(herd, { geometry: true });
      const totalAreaClaim = features.reduce(
        (sum, feature) => sum + (Number(feature.attributes.area_claim) || 0),
        0
      );
      return { totalAreaClaim, locations: features };
    } catch (error) {
      console.error("Error calculating total area_claim: ", error);
      return { totalAreaClaim: 0, locations: [] };
    }
  }
}

export default LPISLayerBuilder;
